import * as moment from 'moment';
import * as React from 'react';
import { Query } from 'react-apollo';

import { DateFilterToolbar } from 'src/app/components/DateFilterToolbar/DateFilterToolbar';
import { FilterOperator } from 'src/app/constants/query/FilterInput';
import { FilterType } from 'src/app/constants/query/FilterType';
import { FETCH_BRANCH_PAGE_QUERY, FetchBranchPageQueryResult } from '../branch/Branch.queries';
import { DASHBOARD_DATE_FORMAT } from './Dashboard.constants';
import { DashboardRangePresets } from './DashboardRangePresets';

interface DashboardFilterBarProps {
  branchId?: number;
  endDate: string;
  onBranchChange: (branchId?: number) => void;
  onRangeChange: (startDate: string, endDate: string) => void;
  startDate: string;
}

interface Branch {
  id: string;
  name: string;
}

/**
 * FetchBranchPageQueryResult is the inner page shape; a direct <Query> receives it under the
 * `branchPage` root field (the shared Table component unwraps that for its own callers).
 */
interface BranchPageQueryData {
  branchPage: FetchBranchPageQueryResult;
}

const ALL_BRANCHES_VALUE = 'ALL';
const BRANCH_PAGE_SIZE = 500;

/**
 * Test branches hidden from the picker. They are already stripped from every figure by
 * EXCLUDED_BRANCH_NAMES in DashboardService, so leaving them selectable would only offer a branch
 * whose dashboard is guaranteed to be empty.
 *
 * Duplicated here rather than imported: the frontend has no `@oasis/common` dependency, the same
 * reason OrderLogActionDisplay is duplicated. Keep the two lists in step.
 */
const EXCLUDED_BRANCH_NAMES = ['asd'];

/**
 * DateFilterToolbar hands back exactly the offset-bearing string the comment above warns about
 * (`moment(date).startOf('day').format()`), so it is flattened to a calendar day before it leaves
 * this component.
 *
 * `parseZone` keeps the offset the toolbar wrote instead of converting into the browser's own —
 * plain `moment(...)` would turn the 1 Sep a user in UTC-5 picked into 31 Aug.
 */
const toCalendarDay = (value?: string): string =>
  value ? moment.parseZone(value).format(DASHBOARD_DATE_FORMAT) : '';

export class DashboardFilterBar extends React.Component<DashboardFilterBarProps> {
  render() {
    const { branchId, endDate, startDate } = this.props;

    return (
      <div className="Dashboard__filter-bar">
        <div className="Dashboard__filter">
          <label className="Dashboard__filter-label">Period</label>
          {/*
            The shared toolbar rather than two pickers of our own: it already enforces the same
            31-day cap the backend clamps to, and already stops the end date landing before the
            start. It also applies on the Filter button instead of on every change, so moving both
            dates costs one query rather than two — worth having while the dashboard's cold-start
            cost at production scale is still unmeasured.

            `showMaximumDuration` is deliberately NOT passed. The toolbar renders its warning on
            `showMaximumDuration || isMaximumDurationExceeded()`, so setting it pins the red text on
            screen no matter how short the range is. Left off, the warning appears only when the
            range really exceeds 31 days — the behaviour of every other page using this toolbar.
          */}
          <DateFilterToolbar
            initialStartDate={startDate}
            initialEndDate={endDate}
            onFilterClick={this.handleFilterClick}
          />
          {/*
            Below the pickers, not instead of them: the presets cover the ranges ops reaches for
            every day, while an ad-hoc request like "dua minggu awal September" still goes through
            the pickers. DateFilterToolbar resyncs from initialStartDate/initialEndDate, so a preset
            click moves the pickers too rather than leaving them showing a stale range.
          */}
          <DashboardRangePresets endDate={endDate} onSelect={this.props.onRangeChange} startDate={startDate} />
        </div>

        <div className="Dashboard__filter">
          <label className="Dashboard__filter-label">Branch</label>
          <Query<BranchPageQueryData>
            query={FETCH_BRANCH_PAGE_QUERY}
            variables={{
              pageInput: {
                page: 1,
                size: BRANCH_PAGE_SIZE,
                filters: {
                  operator: FilterOperator.AND,
                  options: [{ field: 'active', values: ['1'], type: FilterType.EQUAL }]
                }
              }
            }}
          >
            {({ loading, data }) => {
              const branches: Branch[] =
                !loading && data && data.branchPage && data.branchPage.results
                  ? data.branchPage.results.filter(
                      (branch: Branch) => EXCLUDED_BRANCH_NAMES.indexOf(branch.name) === -1
                    )
                  : [];

              return (
                <select
                  className="Dashboard__select"
                  onChange={this.handleBranchChange}
                  value={branchId === undefined ? ALL_BRANCHES_VALUE : String(branchId)}
                >
                  <option value={ALL_BRANCHES_VALUE}>All Branches</option>
                  {branches.map((branch: Branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              );
            }}
          </Query>
        </div>
      </div>
    );
  }

  /**
   * A half-filled range is ignored rather than sent: the toolbar leaves the Filter button live when
   * only one picker is set, and an empty boundary would widen the query to whatever the backend
   * clamp allows instead of what the admin meant.
   */
  private readonly handleFilterClick = (startDate?: string, endDate?: string) => {
    const start = toCalendarDay(startDate);
    const end = toCalendarDay(endDate);
    if (!start || !end) {
      return;
    }
    this.props.onRangeChange(start, end);
  };

  private readonly handleBranchChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    this.props.onBranchChange(value === ALL_BRANCHES_VALUE ? undefined : Number(value));
  };
}
